import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import CompetitionTable from '../../../components/tables/CompetitionTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useCompetitionApi } from '../../../hooks/useCompetitionApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { CompetitionDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';

const CompetitionList: React.FC = () => {
  const { search, remove, extract, isLoading, error } = useCompetitionApi();
  const [items, setItems] = useState<CompetitionDTO[]>([]);
  
  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSort,
    setSearchFilters,
    clearSearchState,
  } = useSearchState('competitions', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
  });
  
  const [totalRecords, setTotalRecords] = useState(0);

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Competitions', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Competitions:', error);
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSort(sortKey, !sortDescending);
    } else {
      setSort(sortKey, true);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, searchFilters);
  }, [currentPage, pageSize, sortBy, sortDescending, searchFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: CompetitionDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, searchFilters);
    } catch (error) {
      console.error('Error deleting Competition:', error);
    }
  };

  // Add export handler
  const handleExport = async () => {
    try {
      const response = await extract({
        filters: searchFilters,
        page: 1,
        pageSize: 9999999, // Large number to export all matching records
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting Competitions:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/competitions"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Manage.Competition.View"
    >
      <Card
        title="Manage Competitions"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex space-x-2">
            <Button type="button" variant="secondary" onClick={handleExport}>
              Export
            </Button>
            <AddNewButton
              onClick={() => navigate('/admin/competitions/create')}
            />
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="name"
              label="Search by Name"
              value={searchFilters.name || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, name: e.target.value })
              }
              placeholder="Search by Name"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <CompetitionTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/competitions/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
          sortBy={sortBy}
          sortDescending={sortDescending}
          onSort={handleSort}
        />
      </Card>
    </AdminLayout>
  );
};

export default CompetitionList;
